import { useId, useRef, useState, type FormEvent } from 'react'
import { Icon } from '@/components/brand/Brand'
import { siteConfig } from '@/config/site'
import { communityCopy, fillCopy } from '@/nominations/communityCopy'
import { NominationAvatar } from '@/nominations/components/NominationAvatar'
import { submitNominationResponse } from '@/nominations/submitNominationResponse'
import type { NominationConfig, NominationDecision, NominationResponsePayload } from '@/nominations/types'

type NominationResponsePanelProps = {
  nomination: NominationConfig
  titleId: string
}

type PanelState = 'idle' | 'submitting' | 'declining' | 'accepted' | 'declined' | 'error' | 'decline-error'

const buildPayload = (
  nomination: NominationConfig,
  decision: NominationDecision,
  fields: { email?: string; topic?: string },
): NominationResponsePayload => ({
  event: 'nomination.response',
  decision,
  slug: nomination.slug,
  path: nomination.path,
  pageUrl: `${siteConfig.siteUrl}${nomination.path}`,
  nominationNumber: nomination.number,
  nominee: {
    name: nomination.nominee.fullName,
    firm: nomination.nominee.firm,
    role: nomination.nominee.role ?? '',
    location: nomination.nominee.location,
    linkedinUrl: nomination.nominee.linkedinUrl,
  },
  nominator: nomination.nominator.fullName,
  reviewer: nomination.reviewer.fullName,
  email: fields.email?.trim() || undefined,
  topic: fields.topic?.trim() || undefined,
  submittedAt: new Date().toISOString(),
})

export const NominationResponsePanel = ({ nomination, titleId }: NominationResponsePanelProps) => {
  const { nominee, reviewer } = nomination
  const [state, setState] = useState<PanelState>('idle')
  // Synchronous guard also catches two actions before React has rendered disabled buttons.
  const inFlight = useRef(false)
  const [email, setEmail] = useState(nominee.email ?? '')
  const [topic, setTopic] = useState('')
  const emailId = useId()
  const topicId = useId()
  const statusId = useId()

  const tokens = {
    nomineeFirst: nominee.firstName,
    reviewerFirst: reviewer.firstName,
    reviewerEmail: reviewer.email,
    email,
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setState('submitting')

    try {
      await submitNominationResponse(buildPayload(nomination, 'accepted', { email, topic }))
      setState('accepted')
    } catch (error) {
      console.error('[nominations] confirm failed', error)
      setState('error')
    } finally {
      inFlight.current = false
    }
  }

  const handleDecline = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setState('declining')

    try {
      await submitNominationResponse(buildPayload(nomination, 'declined', {}))
      setState('declined')
    } catch (error) {
      console.error('[nominations] decline was not recorded', error)
      setState('decline-error')
    } finally {
      inFlight.current = false
    }
  }

  const isBusy = state === 'submitting' || state === 'declining'
  const isResolved = state === 'accepted' || state === 'declined'

  return (
    <section className="nom-response" aria-labelledby={titleId}>
      <div className="x-wrap nom-response-grid">
        <div className="nom-response-copy">
          <p className="x-eyebrow">{communityCopy.form.eyebrow}</p>
          <h2 id={titleId} className="nom-response-title">
            {communityCopy.form.title}
          </h2>
          <div className="nom-reviewer">
            <NominationAvatar monogram={reviewer.monogram} photo={reviewer.photo} size="md" onDark />
            <div>
              <p className="nom-reviewer-statement">{fillCopy(communityCopy.reviewerStatement, tokens)}</p>
              <p className="nom-reviewer-sub">{communityCopy.reviewerSub}</p>
            </div>
          </div>
        </div>

        <div className="nom-response-card">
          {isResolved ? (
            <div className="nom-result" role="status" aria-live="polite">
              <span className="nom-result-icon" aria-hidden>
                <Icon name="check" />
              </span>
              <h3 className="nom-result-title">
                {state === 'accepted' ? fillCopy(communityCopy.form.acceptedTitle, tokens) : communityCopy.form.declinedTitle}
              </h3>
              <p className="nom-result-body">
                {state === 'accepted' ? fillCopy(communityCopy.form.acceptedBody, tokens) : communityCopy.form.declinedBody}
              </p>
            </div>
          ) : (
            <form className="nom-form" onSubmit={handleSubmit} noValidate={false} aria-describedby={statusId}>
              <div className="nom-field">
                <label className="nom-field-label" htmlFor={emailId}>
                  {fillCopy(communityCopy.form.emailLabel, tokens)}
                </label>
                <input
                  id={emailId}
                  className="nom-field-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  placeholder={communityCopy.form.emailPlaceholder}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={isBusy}
                />
              </div>

              <div className="nom-field">
                <label className="nom-field-label" htmlFor={topicId}>
                  {communityCopy.form.topicLabel} <span className="nom-field-optional">({communityCopy.form.optional})</span>
                </label>
                <textarea
                  id={topicId}
                  className="nom-field-input nom-field-input--multiline"
                  name="topic"
                  rows={4}
                  placeholder={nomination.topicPlaceholder}
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  disabled={isBusy}
                />
              </div>

              <button className="btn btn-signal nom-submit" type="submit" disabled={isBusy}>
                {state === 'submitting' ? communityCopy.form.submitting : communityCopy.form.submit}
                <Icon name="arrow" />
              </button>

              <p id={statusId} className="nom-form-status" role="status" aria-live="polite">
                {state === 'error' || state === 'decline-error' ? (
                  <span className="nom-form-error">
                    {fillCopy(state === 'decline-error' ? communityCopy.form.declineErrorBody : communityCopy.form.errorBody, tokens)}
                  </span>
                ) : (
                  communityCopy.form.finePrint
                )}
              </p>

              <button className="nom-decline" type="button" onClick={handleDecline} disabled={isBusy}>
                {state === 'declining'
                  ? communityCopy.form.submitting
                  : state === 'decline-error'
                    ? communityCopy.form.declineRetry
                    : communityCopy.form.decline}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
